import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  getClientReportById,
  getClientReportPdf,
  getClientReportPdfSend,
  updateClientReportByClientId,
} from "../../services";
import Styles from "../../styles/client/viewClientReport.module.scss";
import Navbar from "../../components/Navbar";
import { coachRoutes } from "../../routers/CoachRouters";
import { useDispatch, useSelector } from "react-redux";
import { toggle } from "../../redux";
import Close_Button from "../../assets/auth/close.png";
import Suceess from "../../assets/auth/success.png";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { ApiEndpoints, LocalStorageKeys } from "../../core";

const ViewClientReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isSidebarOpen = useSelector((state) => state.navbar.button);
  const { report } = useSelector((state) => state.client);
  const { clientId } = useParams();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const firstReport =
    reportData && reportData.length > 0 ? reportData[0] : null;

  // New state for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  // validation errors for edit biometric inputs
  const [editErrors, setEditErrors] = useState({
    weight: "",
    muscle: "",
    fat: "",
  });

  // Validate biometric field (weight/muscle max 200, fat max 100, non-negative, numeric)
  const validateEditBiometric = (field, value) => {
    if (value === "" || value === null || value === undefined) {
      setEditErrors((p) => ({ ...p, [field]: "" }));
      return true;
    }
    const n = parseFloat(value);
    if (Number.isNaN(n)) {
      setEditErrors((p) => ({ ...p, [field]: "Invalid value" }));
      return false;
    }
    if (n < 0) {
      setEditErrors((p) => ({ ...p, [field]: "Value cannot be negative" }));
      return false;
    }
    if ((field === "weight" || field === "muscle") && n > 200) {
      setEditErrors((p) => ({ ...p, [field]: "Maximum value is 200" }));
      return false;
    }
    if (field === "fat" && n > 100) {
      setEditErrors((p) => ({ ...p, [field]: "Maximum value is 100" }));
      return false;
    }
    setEditErrors((p) => ({ ...p, [field]: "" }));
    return true;
  };

  useEffect(() => {
    if (isEditing && editData.workouts) {
      const updatedWorkouts = editData.workouts.map((workout, index) => {
        const newPercentage = calculatePercentage(workout);

        return {
          ...workout,
          totalPercentage: newPercentage,
        };
      });

      const totalsChanged = updatedWorkouts.some(
        (workout, index) =>
          workout.totalPercentage !== editData.workouts[index]?.totalPercentage
      );

      if (totalsChanged) {
        console.log(
          "🔄 WHILE EDITING - Workout totals changed, updating state:",
          {
            updatedWorkouts,
            timestamp: new Date().toISOString(),
          }
        );

        setEditData((prev) => ({
          ...prev,
          workouts: updatedWorkouts,
        }));
      }
    }
  }, [isEditing]);

  useEffect(() => {
    const fetchClientReport = async () => {
      try {
        setLoading(true);
        const response = await getClientReportById(clientId);

        setReportData(response);
        setError(null);
      } catch (err) {
        console.error("❌ Error fetching client report:", err);
        setError(err.message || "Failed to fetch client report");
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchClientReport();
    }
  }, [clientId]);

  // Calculate percentage based on scoreAchieved / predefinedScore.max * 100
  const calculatePercentage = (workout) => {
    const { scoreAchieved, predefinedScore } = workout;

    if (!scoreAchieved || !predefinedScore || !predefinedScore.max) {
      return 0;
    }

    const percentage =
      (Number(scoreAchieved) / Number(predefinedScore.max)) * 100;
    return Math.max(0, Math.min(100, percentage));
  };

  // Handle workout field changes during editing
  const handleWorkoutChange = (workoutIndex, field, value) => {
    setEditData((prev) => {
      const updatedWorkouts = prev.workouts.map((workout, index) => {
        if (index === workoutIndex) {
          let updatedWorkout = { ...workout };
          updatedWorkout[field] = value;

          // Recalculate percentage when scoreAchieved changes
          if (field === "scoreAchieved") {
            const newPercentage = calculatePercentage(updatedWorkout);
            console.log(newPercentage);

            updatedWorkout.totalPercentage = newPercentage;
          }

          console.log("🔄 WHILE EDITING - Updated workout:", {
            workoutIndex,
            updatedWorkout,
            timestamp: new Date().toISOString(),
          });

          return updatedWorkout;
        }
        return workout;
      });

      return {
        ...prev,
        workouts: updatedWorkouts,
      };
    });
  };

  const toggleBlock = (blockId) => {
    setExpandedBlocks((prev) => ({
      ...prev,
      [blockId]: !prev[blockId],
    }));
  };

  const handleMainContainerClick = () => {
    if (isSidebarOpen) {
      dispatch(toggle());
    }
  };

  const handlePreview = () => {
    if (isEditing) {
      handleSave();
    } else {
      navigate(
        coachRoutes.fitnesReportPreviewsSummary.replace(":clientId", clientId)
      );
    }
  };

  const handleBack = () => {
    if (isEditing) {
      handleCancel();
    } else {
      navigate(coachRoutes.listClient);
    }
  };

  const handleEdit = (blockKey, reportWithData) => {
    setIsEditing(true);
    setEditingBlockId(blockKey);

    const workoutsWithCalculatedTotals = (reportWithData.workouts || []).map(
      (workout, index) => {
        const calculatedPercentage = calculatePercentage(workout);
        console.log(`✏️ EDIT MODE - Preparing workout ${index + 1}:`, {
          workoutName: workout.workout?.name,
          originalData: workout,
          calculatedPercentage,
        });

        return {
          ...workout,
          totalPercentage: calculatedPercentage,
        };
      }
    );

    const editDataToSet = {
      weight: reportWithData.body?.weight || "",
      muscle: reportWithData.body?.muscle || "",
      fat: reportWithData.body?.fat || "",
      notes: reportWithData.notes || "",
      workouts: workoutsWithCalculatedTotals,
    };

    setEditData(editDataToSet);
  };

  const handleSave = async () => {
    try {
      // validate biometric values before saving
      const validWeight = validateEditBiometric("weight", editData.weight);
      const validMuscle = validateEditBiometric("muscle", editData.muscle);
      const validFat = validateEditBiometric("fat", editData.fat);
      if (!validWeight || !validMuscle || !validFat) {
        toast.error(
          "Please fix biometric values before saving. Maximums: weight & muscle = 200, fat = 100."
        );
        return;
      }

      setSaving(true);

      const blockNumber = parseInt(editingBlockId.split("-")[1]);
      const firstReport = reportData[0];
      const selectedClientId = firstReport.client?._id;

      if (!selectedClientId) {
        throw new Error("Client ID not found");
      }

      // Find the specific report block to get its ID
      const currentBlockReport = reportData.find(
        (report) => report.block === blockNumber
      );

      const blockId = currentBlockReport?._id; // Get the actual block ID from the report

      if (!blockId) {
        throw new Error("Block ID not found");
      }

      const processedWorkouts = editData.workouts.map((workout, index) => {
        const processed = {
          workout: workout.workout?._id || workout._id,
          unit: workout.unit,
          reps: workout?.unit === "KG" ? parseInt(workout.reps) : undefined,
          duration:
            workout?.unit === "CALS" ? parseInt(workout.duration) : undefined,
          scoreAchieved: parseFloat(workout.scoreAchieved) || 0,
          unifiedMetricScore: parseFloat(workout.totalPercentage) || 0,
          predefinedScore: workout.predefinedScore,
        };

        return processed;
      });

      // Safely build body object to avoid NaN when fields are empty
      const toNum = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : undefined;
      };
      const hasAnyBiometric =
        editData.weight !== "" || editData.muscle !== "" || editData.fat !== "";
      const body = hasAnyBiometric
        ? {
            ...(toNum(editData.weight) !== undefined && {
              weight: toNum(editData.weight),
            }),
            ...(toNum(editData.muscle) !== undefined && {
              muscle: toNum(editData.muscle),
            }),
            ...(toNum(editData.fat) !== undefined && {
              fat: toNum(editData.fat),
            }),
          }
        : null;

      const dataToSend = {
        client: selectedClientId,
        block: blockNumber,
        body,
        notes: editData.notes || "",
        workouts: processedWorkouts,
      };

      // Send the block ID and data to the server
      const response = await updateClientReportByClientId(blockId, dataToSend);

      if (response && response._id) {
        setReportData((prevData) => {
          const updatedData = prevData.map((report) => {
            if (report._id === response._id) {
              return {
                ...response,
                workouts:
                  response.workouts?.map((workout) => ({
                    ...workout,
                    totalPercentage: calculatePercentage(workout),
                  })) || [],
              };
            }
            return report;
          });

          return updatedData;
        });
      }

      setIsEditing(false);
      setEditingBlockId(null);
      setEditData({});
    } catch (error) {
      console.error("❌ SAVING ERROR:", {
        error,
        editingBlockId,
        editData,
        timestamp: new Date().toISOString(),
      });
      toast.error(`Error saving changes: ${error.message}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };
  const handleCancel = () => {
    console.log("❌ CANCELING EDIT MODE:", {
      editingBlockId,
      editData,
      timestamp: new Date().toISOString(),
    });

    setIsEditing(false);
    setEditingBlockId(null);
    setEditData({});
  };

  const handleInputChange = (field, value) => {
    console.log("🔄 WHILE EDITING - Input field change:", {
      field,
      value,
      editingBlockId,
      timestamp: new Date().toISOString(),
    });

    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // validate biometric fields live while editing
    if (field === "weight" || field === "muscle" || field === "fat") {
      validateEditBiometric(field, value);
    }
  };

  const isFieldEditable = (originalValue) => {
    return (
      originalValue !== null &&
      originalValue !== undefined &&
      originalValue !== ""
    );
  };

  const handleSendNote = () => {
    const suceess = getClientReportPdfSend(clientId);
    if (suceess) {
      showSuccessModal();
    } else {
      toast.error("Failed to send note. Please try again.");
    }
  };

  const handleDownloadPDF = async () => {
    if (!clientId) {
      console.error("Client ID not found.");
      return;
    }
    const token = localStorage.getItem(LocalStorageKeys.coachToken);

    const downloadUrl = `${ApiEndpoints.baseApiURL}/fitness-report/download/client/${clientId}`;

    try {
      const success = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (success) {
        showSuccessModal();
      }

      const blob = await success.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const clientName = firstReport.client?.name || "";
      const year = firstReport?.year || "";

      const sanitizedClientName = clientName
        .replace(/[^a-zA-Z0-9\s-_]/g, "")
        .replace(/\s+/g, " ")
        .toLowerCase();

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${sanitizedClientName}-${year}-report.pdf`;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(blobUrl); // ✅ Clean up
    } catch (error) {
      console.error("PDF download failed:", error);
    }
  };
  const showSuccessModal = () => {
    Swal.fire({
      html: `
        <div style="text-align: center; border-radius: 20px; position: relative;">
         <button 
            id="customCloseButton" 
            style="position: absolute; top:-10px; right:-20px; background: none; border: none; cursor: pointer;">
            <img 
            src=${Close_Button} 
              alt="Close" 
              style="width: 50px; height: 50px;"
            />
          </button>
          <img 
            src=${Suceess} 
            alt="Success" 
            style="width: 100px; margin-bottom: 20px;margin-top:30px;" 
          />
         
          
        <p style="color: #151515; text-align:center; font-family:gothambold; font-size:24px;">
           Successfull 
          </p>

<br/>
        <button 
            id="ok" 
style="background: white; color: #151515;  padding: 15px 20px; font-family:gothambold; border-radius: 8px; cursor: pointer; margin-top: 20px;border: 1px solid #E27808;width:250px;height:48px;font-size:14px;">
ok  </button>
        </div>
      `,
      showConfirmButton: false,
      customClass: {
        popup: "custom-swal-container",
      },
      didOpen: () => {
        const ok = document.getElementById("ok");
        const closeButton = document.getElementById("customCloseButton");

        ok.addEventListener("click", () => {
          Swal.close();
          navigate(coachRoutes.listClient);
        });

        closeButton.addEventListener("click", () => {
          Swal.close();
          navigate(coachRoutes.listClient);
        });
      },
    });
  };
  if (loading) {
    return <div className={Styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div className={Styles.error}>Error: {error}</div>;
  }

  if (!reportData?.length) {
    return <div className={Styles.noData}>No report data found</div>;
  }

  const groupedReports = reportData.reduce((acc, report) => {
    const key = `block-${report.block}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(report);
    return acc;
  }, {});

  return (
    <>
      <Navbar />
      <div
        className={Styles.exercise_list_container_main}
        onClick={handleMainContainerClick}
      >
        <h2>
          <Link to={coachRoutes.listClient}>HOME / </Link>
          <span>CLIENT REPORT</span>
        </h2>
        <div className={Styles.exercise_list_container}>
          {/* Header Section */}
          <div className={Styles.table_container_heading}>
            <div className={Styles.table_container_headingone}>
              <h3>CLIENT REPORT</h3>
            </div>
            <div className={Styles.table_container_headingtwo}>
              <div
                className={Styles.create_new_session}
                onClick={handlePreview}
                style={{
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? "Saving..." : isEditing ? "Save" : "Preview"}
              </div>
              <div
                className={Styles.back}
                onClick={handleBack}
                style={{ cursor: "pointer" }}
              >
                {isEditing ? "Cancel" : "Back"}
              </div>
            </div>
          </div>

          {/* Client Info Section */}
          <div className={Styles.client_info_section}>
            <div className={Styles.client_info_row}>
              <div className={Styles.info_item}>
                <span className={Styles.label}>Client Name:</span>
                <span className={Styles.value}>
                  {firstReport.client?.name || "-"}
                </span>
              </div>
              <div className={Styles.info_item}>
                <span className={Styles.label}>Unique ID:</span>
                <span className={Styles.value}>
                  {firstReport.client?.code || "-"}
                </span>
              </div>
              <div className={Styles.info_item}>
                <span className={Styles.label}>Phone Number:</span>
                <span className={Styles.value}>
                  {firstReport.client?.phone || "-"}
                </span>
              </div>
              <div className={Styles.info_item}>
                <span className={Styles.label}>Email ID:</span>
                <span className={Styles.value}>
                  {firstReport.client?.email || "-"}
                </span>
              </div>
            </div>
          </div>

          {Object.entries(groupedReports)
            .sort(([a], [b]) => {
              const blockA = parseInt(a.split("-")[1]);
              const blockB = parseInt(b.split("-")[1]);
              return blockB - blockA;
            })
            .map(([blockKey, reports]) => {
              const blockNumber = reports[0].block;
              const reportWithData =
                reports.find((r) => r.body || r.workouts) || reports[0];
              const isExpanded = expandedBlocks[blockKey];
              const isCurrentlyEditing =
                isEditing && editingBlockId === blockKey;

              return (
                <div key={blockKey} className={Styles.report_block}>
                  <div
                    className={Styles.block_header}
                    onClick={() => toggleBlock(blockKey)}
                  >
                    <div className={Styles.block_info}>
                      <span className={Styles.block_title}>
                        Block: {blockNumber}
                      </span>
                      <span className={Styles.block_date}>
                        Date:{" "}
                        {new Date(
                          reportWithData.createdAt
                        ).toLocaleDateString()}
                      </span>
                    </div>
                    <div className={Styles.expand_icon}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        className={`${Styles.dropdown_arrow} ${
                          isExpanded ? Styles.expanded : ""
                        }`}
                      >
                        <path
                          d="M6 9l6 6 6-6"
                          stroke="#6C6062"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  {isExpanded && (
                    <div
                      className={`${Styles.block_content} ${
                        isCurrentlyEditing ? Styles.editing_mode : ""
                      }`}
                    >
                      <div className={Styles.content_sections}>
                        <div className={Styles.biometrics_section}>
                          <h4>Biometrics</h4>
                          <div
                            className={`${Styles.biometrics_table} ${
                              isCurrentlyEditing ? Styles.editing_mode : ""
                            }`}
                          >
                            <div className={Styles.biometric_row}>
                              <span className={Styles.metric_label}>
                                Body Weight (kg)
                              </span>
                              <span className={Styles.metric_value}>
                                {isCurrentlyEditing ? (
                                  <>
                                    <input
                                      type="number"
                                      value={editData.weight}
                                      onChange={(e) =>
                                        handleInputChange(
                                          "weight",
                                          e.target.value
                                        )
                                      }
                                      className={Styles.edit_input}
                                      placeholder="Enter weight"
                                      disabled={false}
                                      min={0}
                                      max={200}
                                    />
                                    {editErrors.weight && (
                                      <div
                                        style={{
                                          color: "#D32F2F",
                                          fontSize: 12,
                                          marginTop: 4,
                                        }}
                                      >
                                        {editErrors.weight}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  reportWithData.body?.weight || "-"
                                )}
                              </span>
                            </div>

                            <div className={Styles.biometric_row}>
                              <span className={Styles.metric_label}>
                                Muscle (kg)
                              </span>
                              <span className={Styles.metric_value}>
                                {isCurrentlyEditing ? (
                                  <>
                                    <input
                                      type="number"
                                      value={editData.muscle}
                                      onChange={(e) =>
                                        handleInputChange(
                                          "muscle",
                                          e.target.value
                                        )
                                      }
                                      className={Styles.edit_input}
                                      placeholder="Enter muscle"
                                      disabled={false}
                                      min={0}
                                      max={200}
                                    />
                                    {editErrors.muscle && (
                                      <div
                                        style={{
                                          color: "#D32F2F",
                                          fontSize: 12,
                                          marginTop: 4,
                                        }}
                                      >
                                        {editErrors.muscle}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  reportWithData.body?.muscle || "-"
                                )}
                              </span>
                            </div>
                            <div className={Styles.biometric_row}>
                              <span className={Styles.metric_label}>
                                Body Fat (%)
                              </span>
                              <span className={Styles.metric_value}>
                                {isCurrentlyEditing ? (
                                  <>
                                    <input
                                      type="number"
                                      value={editData.fat}
                                      onChange={(e) =>
                                        handleInputChange("fat", e.target.value)
                                      }
                                      className={Styles.edit_input}
                                      placeholder="Enter body fat %"
                                      disabled={false}
                                      min={0}
                                      max={100}
                                      step="0.1"
                                    />
                                    {editErrors.fat && (
                                      <div
                                        style={{
                                          color: "#D32F2F",
                                          fontSize: 12,
                                          marginTop: 4,
                                        }}
                                      >
                                        {editErrors.fat}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  reportWithData.body?.fat || "-"
                                )}
                              </span>
                            </div>
                          </div>

                          {/* Notes Section */}
                          <div className={Styles.notes_section}>
                            <h5>Notes:</h5>
                            {isCurrentlyEditing ? (
                              <textarea
                                value={editData.notes}
                                onChange={(e) =>
                                  handleInputChange("notes", e.target.value)
                                }
                                className={Styles.edit_textarea}
                                placeholder="Enter notes..."
                                rows="4"
                                disabled={false}
                              />
                            ) : (
                              <p>
                                {reportWithData.notes || "No notes available"}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Exercise Section */}
                        <div className={Styles.exercise_section}>
                          <h4>Exercise</h4>

                          {reportWithData.workouts &&
                          reportWithData.workouts.length > 0 ? (
                            <div className={Styles.exercise_table}>
                              <div className={Styles.table_header}>
                                <span>Exercise Name</span>
                                <span>Reps</span>
                                <span>Duration</span>
                                <span>Score Achieved</span>
                                <span>Total (%)</span>
                              </div>
                              {(isCurrentlyEditing
                                ? editData.workouts
                                : reportWithData.workouts
                              ).map((workout, index) => {
                                const originalWorkout =
                                  reportWithData.workouts[index];

                                return (
                                  <div
                                    key={workout._id || index}
                                    className={Styles.table_row}
                                  >
                                    {/* Exercise Name */}
                                    <span>
                                      {workout.workout?.name
                                        ? workout.workout.name
                                            .charAt(0)
                                            .toUpperCase() +
                                          workout.workout.name
                                            .slice(1)
                                            .toLowerCase()
                                        : "-"}
                                    </span>
                                    <span>
                                      {workout.unit === "KG" ? (
                                        isCurrentlyEditing ? (
                                          <input
                                            type="number"
                                            value={workout.reps || ""}
                                            onChange={(e) =>
                                              handleWorkoutChange(
                                                index,
                                                "reps",
                                                e.target.value
                                              )
                                            }
                                            className={`${
                                              Styles.edit_input_small
                                            } ${
                                              !isFieldEditable(
                                                originalWorkout?.reps
                                              )
                                                ? Styles.disabled_input
                                                : ""
                                            }`}
                                            disabled={
                                              !isFieldEditable(
                                                originalWorkout?.reps
                                              )
                                            }
                                            placeholder="Reps"
                                          />
                                        ) : (
                                          workout.reps || "-"
                                        )
                                      ) : (
                                        "-"
                                      )}
                                    </span>

                                    {/* Duration Column - Show values if unit is CALS */}
                                    <span>
                                      {workout.unit === "CALS" ? (
                                        isCurrentlyEditing ? (
                                          <input
                                            type="number"
                                            value={workout.duration || ""}
                                            onChange={(e) =>
                                              handleWorkoutChange(
                                                index,
                                                "duration",
                                                e.target.value
                                              )
                                            }
                                            className={`${
                                              Styles.edit_input_small
                                            } ${
                                              !isFieldEditable(
                                                originalWorkout?.duration
                                              )
                                                ? Styles.disabled_input
                                                : ""
                                            }`}
                                            disabled={
                                              !isFieldEditable(
                                                originalWorkout?.duration
                                              )
                                            }
                                            placeholder="Minutes"
                                          />
                                        ) : workout.duration ? (
                                          `${workout.duration} Min`
                                        ) : (
                                          "-"
                                        )
                                      ) : (
                                        "-"
                                      )}
                                    </span>

                                    {/* Score Achieved */}
                                    <span>
                                      {isCurrentlyEditing ? (
                                        <input
                                          type="number"
                                          value={workout.scoreAchieved || ""}
                                          onChange={(e) =>
                                            handleWorkoutChange(
                                              index,
                                              "scoreAchieved",
                                              e.target.value
                                            )
                                          }
                                          className={`${
                                            Styles.edit_input_small
                                          } ${
                                            !isFieldEditable(
                                              originalWorkout?.scoreAchieved
                                            )
                                              ? Styles.disabled_input
                                              : ""
                                          }`}
                                          disabled={
                                            !isFieldEditable(
                                              originalWorkout?.scoreAchieved
                                            )
                                          }
                                          placeholder="Score"
                                        />
                                      ) : (
                                        workout.scoreAchieved || "-"
                                      )}
                                    </span>

                                    {/* Total Percentage */}
                                    <span
                                      className={
                                        isCurrentlyEditing
                                          ? Styles.total_calculated
                                          : ""
                                      }
                                    >
                                      {isCurrentlyEditing ? (
                                        <span className={Styles.total_display}>
                                          {calculatePercentage(workout).toFixed(
                                            1
                                          )}
                                          %
                                        </span>
                                      ) : (
                                        `${calculatePercentage(workout).toFixed(
                                          1
                                        )}%`
                                      )}
                                    </span>
                                  </div>
                                );
                              })}

                              {!isCurrentlyEditing && (
                                <div
                                  className={`${Styles.table_row} ${Styles.grand_total_row}`}
                                >
                                  <span>
                                    <strong>Grand Total</strong>
                                  </span>
                                  <span></span>
                                  <span></span>
                                  <span></span>
                                  <span>
                                    <strong>
                                      {reportWithData.overallUnifiedMetricScore
                                        ? `${reportWithData.overallUnifiedMetricScore.toFixed(
                                            2
                                          )}%`
                                        : "0%"}
                                    </strong>
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className={Styles.no_exercises}>
                              No exercises recorded
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Button */}
                      {!isEditing && (
                        <div className={Styles.block_actions}>
                          <button
                            className={Styles.edit_button}
                            onClick={() => handleEdit(blockKey, reportWithData)}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

          {/* Bottom Actions - Hide when editing */}
          {!isEditing && (
            <div className={Styles.bottom_actions}>
              <button
                className={Styles.send_note_button}
                onClick={handleSendNote}
              >
                Send Now
              </button>
              <button
                className={Styles.download_button}
                onClick={handleDownloadPDF}
              >
                Download PDF
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ViewClientReport;
